import { Lightning } from "@phosphor-icons/react";

const H2 = ({ children }) => (
    <h2 className="font-heading font-black text-lg uppercase tracking-tight text-ink mt-8 mb-3">
        {children}
    </h2>
);

const P = ({ children }) => (
    <p className="text-sm text-neutral-800 leading-relaxed mb-3">{children}</p>
);

const LegalHeader = ({ title, updated }) => (
    <header className="border-b border-neutral-200">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
            <a
                href="/"
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
                <Lightning size={18} weight="bold" className="text-klein" />
                <span className="font-heading font-black uppercase tracking-tight text-base text-ink">
                    SerpScout
                </span>
            </a>
            <span className="font-mono uppercase tracking-widest text-[10px] font-bold text-neutral-500">
                Updated {updated}
            </span>
        </div>
    </header>
);

const LegalFooter = () => (
    <footer className="border-t border-neutral-200 mt-12">
        <div className="max-w-3xl mx-auto px-6 py-6 flex flex-wrap items-center justify-between gap-4 text-xs text-neutral-600">
            <div>
                © {new Date().getFullYear()} Metro Berry Digital Marketing
                Services
            </div>
            <div className="flex items-center gap-4">
                <a
                    href="/"
                    className="hover:text-klein underline underline-offset-4"
                >
                    Home
                </a>
                <a
                    href="/privacy-policy"
                    className="hover:text-klein underline underline-offset-4"
                >
                    Privacy
                </a>
                <a
                    href="/terms-of-service"
                    className="hover:text-klein underline underline-offset-4"
                >
                    Terms
                </a>
            </div>
        </div>
    </footer>
);

export function PrivacyPolicy() {
    return (
        <div className="min-h-screen bg-white text-ink">
            <LegalHeader title="Privacy Policy" updated="17 Feb 2026" />
            <main className="max-w-3xl mx-auto px-6 py-10">
                <h1 className="font-heading font-black text-4xl uppercase tracking-tight text-ink">
                    Privacy Policy
                </h1>
                <p className="text-xs font-mono uppercase tracking-widest text-neutral-500 mt-2">
                    Effective 17 February 2026
                </p>

                <P>
                    This Privacy Policy explains what personal information
                    SerpScout ("we", "our", or "the app") — operated by Metro
                    Berry Digital Marketing Services — collects when you use the
                    app at{" "}
                    <a
                        href="https://metroberrygoogleadsbuilder.com"
                        className="underline underline-offset-4 text-klein"
                    >
                        metroberrygoogleadsbuilder.com
                    </a>
                    , how we use it, and the choices you have.
                </P>

                <H2>1. Who we are</H2>
                <P>
                    SerpScout is a Google Ads campaign management tool built
                    and operated by Metro Berry Digital Marketing Services (the
                    "Company"). If you have any questions about this policy or
                    your data, contact us at{" "}
                    <a
                        href="mailto:rameshpuranam100@gmail.com"
                        className="underline underline-offset-4 text-klein"
                    >
                        rameshpuranam100@gmail.com
                    </a>
                    .
                </P>

                <H2>2. What we collect</H2>
                <P>
                    <b>Account information.</b> When you sign in with Google we
                    receive your name, email address, profile picture URL, and
                    a Google user ID. We store these to identify you inside
                    SerpScout.
                </P>
                <P>
                    <b>Google Ads data.</b> If you connect a Google Ads account
                    to SerpScout, we access your Google Ads account structure
                    (campaigns, ad groups, keywords, ads) and performance
                    metrics (impressions, clicks, cost, conversions, search
                    terms). This access is granted via the{" "}
                    <code className="text-xs bg-neutral-100 border px-1">
                        https://www.googleapis.com/auth/adwords
                    </code>{" "}
                    OAuth scope and can be revoked by you at any time.
                </P>
                <P>
                    <b>Content you provide.</b> Search queries, keywords, ad
                    copy drafts, uploaded CSVs, notes, and other inputs you
                    enter into SerpScout.
                </P>
                <P>
                    <b>Usage & technical data.</b> Standard log data such as IP
                    address, browser type, timestamps and the pages/features
                    you use — used for security, debugging, and improving the
                    product.
                </P>

                <H2>3. How we use your data</H2>
                <P>
                    We use the data described above solely to provide the
                    features you use inside SerpScout, including:
                </P>
                <ul className="list-disc pl-6 text-sm text-neutral-800 leading-relaxed space-y-1 mb-3">
                    <li>
                        Displaying the structure and performance of your
                        Google Ads account.
                    </li>
                    <li>
                        Creating or updating campaigns, ad groups, keywords and
                        ads at your explicit direction.
                    </li>
                    <li>
                        Generating exports (Google Ads Editor-compatible CSVs,
                        PDF audits) for you to download.
                    </li>
                    <li>
                        Providing AI-powered recommendations (e.g. bid
                        adjustments, negative keywords) that you can review
                        before applying.
                    </li>
                </ul>

                <H2>4. Google user data — Limited Use compliance</H2>
                <P>
                    SerpScout's use of information received from Google APIs
                    complies with the{" "}
                    <a
                        href="https://developers.google.com/terms/api-services-user-data-policy#limited-use"
                        target="_blank"
                        rel="noreferrer"
                        className="underline underline-offset-4 text-klein"
                    >
                        Google API Services User Data Policy
                    </a>
                    , including the Limited Use requirements. Specifically:
                </P>
                <ul className="list-disc pl-6 text-sm text-neutral-800 leading-relaxed space-y-1 mb-3">
                    <li>
                        We do not use Google user data (including Google Ads
                        data obtained through the adwords scope) to serve
                        advertisements to any user.
                    </li>
                    <li>
                        We do not sell, rent, or transfer Google user data to
                        third parties.
                    </li>
                    <li>
                        We do not use Google user data to train, develop, or
                        improve generalized machine learning models. AI
                        recommendations inside the app are generated
                        transactionally per session and are not used to train
                        any model.
                    </li>
                    <li>
                        Humans do not read Google user data unless we have your
                        explicit consent, it is necessary for security
                        purposes, to comply with law, or the data has been
                        aggregated and anonymised.
                    </li>
                </ul>

                <H2>5. Who we share data with</H2>
                <P>
                    We share data only with the service providers that make
                    SerpScout run:
                </P>
                <ul className="list-disc pl-6 text-sm text-neutral-800 leading-relaxed space-y-1 mb-3">
                    <li>
                        <b>Google (Ads API, OAuth):</b> to authenticate you and
                        interact with your Google Ads account on your behalf.
                    </li>
                    <li>
                        <b>MongoDB (cloud database):</b> to store your account
                        record and the campaign audit history you generate
                        inside the app.
                    </li>
                    <li>
                        <b>DataForSEO:</b> to fetch supplemental SERP and
                        keyword data (only the queries you type — not your
                        Google Ads data).
                    </li>
                    <li>
                        <b>AI providers (Anthropic, Google Gemini, OpenAI):</b>{" "}
                        used transactionally to generate recommendations. Only
                        the anonymised inputs needed for the specific
                        recommendation are sent. Providers are contractually
                        prohibited from using this data to train their models.
                    </li>
                    <li>
                        <b>Hosting:</b> our servers run on standard cloud
                        infrastructure with encryption in transit and at rest.
                    </li>
                </ul>
                <P>
                    We do not sell your personal information or Google user
                    data.
                </P>

                <H2>6. How long we keep it</H2>
                <P>
                    We keep your account and campaign history as long as your
                    account is active. You can request deletion of any or all
                    of your data by emailing us at{" "}
                    <a
                        href="mailto:rameshpuranam100@gmail.com"
                        className="underline underline-offset-4 text-klein"
                    >
                        rameshpuranam100@gmail.com
                    </a>
                    . We honour deletion requests within 30 days.
                </P>

                <H2>7. Your controls</H2>
                <ul className="list-disc pl-6 text-sm text-neutral-800 leading-relaxed space-y-1 mb-3">
                    <li>
                        <b>Revoke Google access at any time</b> from{" "}
                        <a
                            href="https://myaccount.google.com/permissions"
                            target="_blank"
                            rel="noreferrer"
                            className="underline underline-offset-4 text-klein"
                        >
                            Google account permissions
                        </a>
                        . SerpScout will stop being able to read or write to
                        your Google Ads account immediately.
                    </li>
                    <li>
                        <b>Delete your account and all data</b> by emailing
                        support (above).
                    </li>
                    <li>
                        <b>Access or export your data</b> — available on
                        request.
                    </li>
                </ul>

                <H2>8. Security</H2>
                <P>
                    We use encryption in transit (HTTPS) and at rest, restrict
                    server access to authorised personnel, and follow
                    industry-standard practices to protect your data. No
                    system is 100% secure — if you believe your account has
                    been compromised, contact us immediately.
                </P>

                <H2>9. Children</H2>
                <P>
                    SerpScout is not directed to children under 16 and we do
                    not knowingly collect data from them.
                </P>

                <H2>10. Changes to this policy</H2>
                <P>
                    If we change this policy in a material way, we'll update
                    the "Effective" date at the top and notify active users by
                    email at least 7 days before the change takes effect.
                </P>

                <H2>11. Contact</H2>
                <P>
                    Metro Berry Digital Marketing Services
                    <br />
                    Email:{" "}
                    <a
                        href="mailto:rameshpuranam100@gmail.com"
                        className="underline underline-offset-4 text-klein"
                    >
                        rameshpuranam100@gmail.com
                    </a>
                </P>
            </main>
            <LegalFooter />
        </div>
    );
}

export function TermsOfService() {
    return (
        <div className="min-h-screen bg-white text-ink">
            <LegalHeader title="Terms of Service" updated="17 Feb 2026" />
            <main className="max-w-3xl mx-auto px-6 py-10">
                <h1 className="font-heading font-black text-4xl uppercase tracking-tight text-ink">
                    Terms of Service
                </h1>
                <p className="text-xs font-mono uppercase tracking-widest text-neutral-500 mt-2">
                    Effective 17 February 2026
                </p>

                <P>
                    These Terms of Service ("Terms") govern your use of
                    SerpScout at{" "}
                    <a
                        href="https://metroberrygoogleadsbuilder.com"
                        className="underline underline-offset-4 text-klein"
                    >
                        metroberrygoogleadsbuilder.com
                    </a>
                    , operated by Metro Berry Digital Marketing Services (the
                    "Company", "we", or "us"). By signing in to SerpScout, you
                    agree to these Terms.
                </P>

                <H2>1. Who can use SerpScout</H2>
                <P>
                    SerpScout is an internal campaign-management tool provided
                    to authorised users of Metro Berry Digital Marketing
                    Services and to clients whose Google Ads accounts we
                    manage. You must be at least 18 years old and legally able
                    to enter into these Terms.
                </P>

                <H2>2. Your Google Ads account</H2>
                <P>
                    SerpScout only accesses Google Ads accounts you explicitly
                    connect. By connecting an account you confirm that:
                </P>
                <ul className="list-disc pl-6 text-sm text-neutral-800 leading-relaxed space-y-1 mb-3">
                    <li>
                        You either own the account or have the authority from
                        the account owner to authorise SerpScout to access it.
                    </li>
                    <li>
                        You understand that SerpScout can, at your direction,
                        create and modify campaigns, ad groups, keywords, ads,
                        bid modifiers, and demographic exclusions in that
                        account.
                    </li>
                    <li>
                        You are responsible for reviewing recommendations
                        before you click Apply — SerpScout does not push
                        anything to Google Ads without your explicit action.
                    </li>
                </ul>

                <H2>3. Acceptable use</H2>
                <P>You agree not to:</P>
                <ul className="list-disc pl-6 text-sm text-neutral-800 leading-relaxed space-y-1 mb-3">
                    <li>
                        Use SerpScout to violate Google Ads policies, the
                        Google Ads API terms, or any applicable law.
                    </li>
                    <li>
                        Attempt to reverse-engineer, resell, or sublicense
                        SerpScout.
                    </li>
                    <li>
                        Use SerpScout to access Google Ads accounts you are not
                        authorised to manage.
                    </li>
                    <li>
                        Interfere with SerpScout's operation or attempt to
                        gain unauthorised access to other users' data.
                    </li>
                </ul>

                <H2>4. Third-party services</H2>
                <P>
                    SerpScout integrates with Google Ads API, DataForSEO, and
                    third-party AI providers (Anthropic, Google Gemini, OpenAI).
                    Your use of those services through SerpScout is subject
                    to each provider's own terms. We are not responsible for
                    third-party service availability, accuracy, or actions.
                </P>

                <H2>5. AI-generated recommendations</H2>
                <P>
                    SerpScout provides recommendations (keyword ideas, bid
                    adjustments, negative keywords, ad copy variants) generated
                    with the assistance of AI. These recommendations are
                    starting points and require your professional judgement —
                    do not apply them blindly. We are not liable for outcomes
                    of recommendations you choose to apply.
                </P>

                <H2>6. Fees</H2>
                <P>
                    SerpScout is provided at no cost to authorised users of
                    Metro Berry Digital Marketing Services. Third-party costs
                    incurred through your Google Ads account (ad spend,
                    DataForSEO API usage on your credentials, etc.) are your
                    responsibility.
                </P>

                <H2>7. Data and privacy</H2>
                <P>
                    Our use of your data is described in our{" "}
                    <a
                        href="/privacy-policy"
                        className="underline underline-offset-4 text-klein"
                    >
                        Privacy Policy
                    </a>
                    . Google user data obtained through the adwords scope is
                    used only for the campaign-management features you use
                    inside SerpScout and is not sold, shared, or used to train
                    ML models.
                </P>

                <H2>8. Termination</H2>
                <P>
                    You can stop using SerpScout at any time by revoking
                    access from your{" "}
                    <a
                        href="https://myaccount.google.com/permissions"
                        target="_blank"
                        rel="noreferrer"
                        className="underline underline-offset-4 text-klein"
                    >
                        Google account permissions
                    </a>{" "}
                    and requesting account deletion via support. We may
                    suspend or terminate your access if you violate these
                    Terms or if we're required to by law.
                </P>

                <H2>9. Disclaimers</H2>
                <P>
                    SerpScout is provided "as is" without warranties of any
                    kind, express or implied, including merchantability,
                    fitness for a particular purpose, or non-infringement. We
                    do not guarantee that recommendations will improve your
                    Google Ads performance or that the service will be
                    uninterrupted or error-free.
                </P>

                <H2>10. Limitation of liability</H2>
                <P>
                    To the maximum extent permitted by law, Metro Berry
                    Digital Marketing Services will not be liable for any
                    indirect, incidental, special, consequential, or punitive
                    damages, or any loss of profits or revenues arising from
                    your use of SerpScout, even if we've been advised of the
                    possibility of such damages.
                </P>

                <H2>11. Changes</H2>
                <P>
                    We may update these Terms from time to time. Material
                    changes will be communicated by email at least 7 days
                    before they take effect. Continued use of SerpScout after
                    the effective date means you accept the updated Terms.
                </P>

                <H2>12. Contact</H2>
                <P>
                    Metro Berry Digital Marketing Services
                    <br />
                    Email:{" "}
                    <a
                        href="mailto:rameshpuranam100@gmail.com"
                        className="underline underline-offset-4 text-klein"
                    >
                        rameshpuranam100@gmail.com
                    </a>
                </P>
            </main>
            <LegalFooter />
        </div>
    );
}
